'use strict';
namespace TR {

    import BoardType = Core.Hotels.BoardType;

    interface LastMinuteDealsConfig {
        findOptionsPageSize: number;
        findOptionsFlexibility: number;
        smallBatchTake: number;
        halfBatchTake: number;
        halfBatchChunkSize: number;
    }

    interface LastMinuteDealsDestination {
        TypeId: string;
        Type: number;
        Id: number;
        Title: string;
        deals?: any;
    }

    interface FlightsDeal {
        AdultFare: number;
        AdultTax: number;
        CurrencyCode: string;
        DestinationAirport: string;
        DestinationAirportTitle: string;
        FlightId: string;
        InboundArrivalDate: string;
        InboundDepartureDate: string;
        InboundDuration: number;
        InboundStops: number;
        OriginAirport: string;
        OriginAirportTitle: string;
        OutboundArrivalDate: string;
        OutboundDepartureDate: string;
        OutboundDuration: number;
        OutboundStops: number;
        Price: number;
        ReturnArrivalAirport: string;
        ReturnArrivalAirportTitle: string;
        SearchId: string;
        SearchIds: string[];
    }

    interface BoardTypeMinPrices {
        BoardType: BoardType;
        MinPrice: number;
        WasPrice: number;
    }

    interface HotelDeal {
        AvailabilitySearchId: string;
        BoardType: BoardType;
        BoardTypeMinPrices: BoardTypeMinPrices[];
        CheckInDate: string;
        CheckOutDate: string;
        EstabId: number;
        NonRefundable: boolean;
        PayAtHotelAvailable: boolean;
        Price: number;
        RefundableCutOffDate: string;
        WasPrice: number;
    }

    interface HolidayDeals {
        FlightsDeal: FlightsDeal;
        HotelDeal: HotelDeal;
    }

    interface LinkedAirports {
        Code: string;
        DistanceKm: number;
        Latitude: number;
        Longitude: number;
        ShortTitle: string;
        Title: string;
    }

    interface TransferTimes {
        Airport: string;
        Minutes: number;
    }

    interface Weather {
        Month: number;
        Temperature: number;
    }

    interface Deal {
        AcomType: string;
        AcomTypeId: number;
        Address: string;
        Airports: string[];
        BoardType: string;
        CallToBookBehaviour: number;
        Country: string;
        CountryId: number;
        CroppedImage: string;
        CurrentViews: number;
        CustomerRating: number;
        CustomerRatingCount: number;
        CustomerRatingTitle: string;
        DealMinPrice: number;
        DealWasPrice: number;
        Description: string;
        DiscountPercentage: number;
        DisplayIndex: number;
        Duration: number;
        HolidayDeals: HolidayDeals[];
        Id: number;
        ImageUrl: string;
        IndicativeMinPrice: number;
        IsVilla: boolean;
        Latitude: number;
        LinkedAirports: LinkedAirports[];
        Location: string;
        LocationId: number;
        Longitude: number;
        MinCost: number;
        MinPricePerson: number;
        NonRefundable: boolean;
        NumberOfPhotos: number;
        OriginalCost: number;
        PayAtHotelAvailable: boolean;
        Postcode: string;
        PropertyTypeFrom: string;
        Province: string;
        ProvinceId: number;
        Recommended: boolean;
        RoomNightsBooked: number;
        ShortTitle: string;
        StarRating: number;
        StarRatingTitle: string;
        StarRatingV2: number;
        Summary: string;
        Title: string;
        TransferTimes: TransferTimes[];
        Url: string;
        WasPricePerPerson: number;
        Weather: Weather[];
    }

    interface DealChunks {
        SmallBatch: Deal[][];
        FirstHalfBatch: Deal[][];
        SecondHalfBatch: Deal[][];
    }

    let startDateInitialised = TRPCONFIG.Domain.DeviceType !== 'Mobile';

    class SeasonalDeals {

        summary: any;
        blocks: any;
        destinations: any;
        unblockOnce: ()=>void;
        showMore: boolean;
        isLoading: boolean = true;
        hasDeals: boolean = false;
        hasSpecialDeals: boolean = false;
        counter = 0;
        config: LastMinuteDealsConfig;
        priceMode = 0;
        showDiscount: boolean = TRPCONFIG.Domain.SeasonalDeals && TRPCONFIG.Domain.SeasonalDeals.isPillVisible;
        specialDeals: DealChunks | undefined;
        dealConfigs: any;
        isAprilCampaign: boolean = TRPCONFIG.Domain.AprilCampaignRoutes.indexOf(this.$location.path().substr(1)) > -1;
        aprilCampaignConfig: any;

        constructor(
            private $q: ng.IQService,
            private $location: ng.ILocationService,
            private $timeout: ng.ITimeoutService,
            private $scope: any,
            private $rootScope: any,
            private $route: any,
            private $http: any,
            private tripService: any,
            private filtering: any,
            private preferences: any,
            private pageStrand: any,
            private analytics: any,
            private broker: Core.Broker,
            private holidayDeals: any,
            private holidayDealsGrouped: TR.HolidayDealsGrouped,
            private holidayDealsPromoService: any,
            private hotelFromSession: any,
            private geo: any,
            private dateUtil: any,
            private dealsBoxService: any,
            private occupancyService: Core.OccupancyService,
            private searchUnitService: Search.SearchUnitService,
            private scrollService: any,
            private funcUtil: any,
            private resourceService: Core.ResourceService,
            private destinationService: any
        ) {
            this.dealConfigs = _.find(TRPCONFIG.Domain.ActiveSeasonalDeals, d=> d.Url === $location.path());
            TRPCONFIG.Domain.FlightDetailsExpanded = true;
            if (this.isAprilCampaign) {
                this.dealConfigs = _.find(TRPCONFIG.Domain.ActiveSeasonalDeals, d=> d.Url === '/aprilcampaign');
                this.aprilCampaignConfig = TRPCONFIG.Domain.AprilCampaignRoutesConfig[$location.path().substr(1)];
                this.dealConfigs.DealType = this.aprilCampaignConfig.dealType;
            }
            preferences.set('priceMode', 0);
            $scope.navigatorUserAgent = '|' + navigator.userAgent + '|' + /prerender/i.test(navigator.userAgent) + '|';
            this.watchForPageRedirection();

            analytics.track('mixpanel', 'SeasonalDealsPageView', {
                Name: this.dealConfigs && this.dealConfigs.Name
            });

            if (!startDateInitialised) {
                let startDate = this.dateUtil.getNextSaturday(TRPCONFIG.Domain.HolidayDealsDefaultWeeks);
                searchUnitService.startDate(1, startDate);
                startDateInitialised = true;
            }

            this.config = ((): LastMinuteDealsConfig => {
                let configs = {
                    Default: {
                        findOptionsPageSize: 32,
                        smallBatchTake: 12,
                        halfBatchTake: 4,
                        halfBatchChunkSize: 4,
                        slidesToScroll: 4,
                        slidesToShow: 4
                    },
                    Phone: {
                        findOptionsPageSize: 16,
                        smallBatchTake: 4,
                        halfBatchTake: 8,
                        halfBatchChunkSize: 1,
                        slidesToScroll: 1,
                        slidesToShow: 1
                    },
                    Tablet: {
                        findOptionsPageSize: 24,
                        smallBatchTake: 6,
                        halfBatchChunkSize: 3
                    }
                };
                _.merge(configs, TRPCONFIG.Domain.LastMinuteDealsConfig);
                return _.merge(configs.Default, configs[TRPCONFIG.DeviceCategory]);
            })();

            $scope.$emit('block', {
                title: resourceService.get('SEARCHINGDEALS'),
                subtitle: resourceService.get('THISMAYTAKE'),
                loadingScreenStyle: 'default-lastMinuteDeals'
            });
            this.unblockOnce = _.once(() => this.$scope.$emit('unblock'));

            this.$rootScope.hideFooterToolbar = true;

            this.geo.getNearestAirports()
                .then(airposts => this.$http.post(TRPCONFIG.Domain.SeasonalDealApiUrl, {
                    //'/api/deals/seasonal'
                    //http://pp.hotelsapi2.services.travelrepublic.local/api/deals/topdeals
                    DealType: this.dealConfigs.DealType,
                    CultureCode: TRPCONFIG.Domain.CultureCode,
                    DomainId: TRPCONFIG.Domain.DomainId,
                    CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                    DepartureAirports: _.map(airposts, (a: any) => a.Code),
                    DealsPerDestination: 8,
                    FieldFlags: 2166987,
                }))
                .then(({data}) => data)
                .then(destinations => {
                    this.destinations = _(destinations)
                        .map(d => {
                            let a = _.take(d.Hotels, 8);
                            _.forEach(a, a => this.holidayDeals.enrichHotel(a, { Duration: 7 }));
                            let b: {}[] = [];
                            if (TRPCONFIG.DeviceCategory !== 'Phone') {
                                b = a.splice(4);
                            }
                            return {
                                Title: d.Destination.Title,
                                chunks: [a, b],
                            };
                        })
                        .value();
                    this.unblockOnce();
                    this.hasDeals = !!_.find(this.destinations, d => _.get(d, 'chunks[0].length'));
                    this.isLoading = false;
                });
        }

        goToDeal(deal: Deal, DealCategory: string): void {
            this.analytics.track('mixpanel', 'SeasonalDealsCardClick', {
                CardDestination: `${deal.Country}, ${deal.Province}`,
                CardRank: deal.DisplayIndex + 1,
                EstabId: deal.Id,
                OverlayShown: this.showDiscount,
                OverlayValue: `${deal.DiscountPercentage}%`,
                DealCategory,
                Name: this.dealConfigs && this.dealConfigs.Name
            });

            let occupancy = { Adults: 2, ChildAges: [] };
            let selected = deal.HolidayDeals[0];
            selected.HotelDeal.EstabId = deal.Id;

            this.$scope.$emit('block', {
                title: this.resourceService.get('LOADINGHOTELS'),
                subtitle: this.resourceService.get('THISMAYTAKE'),
                loadingScreenStyle: 'default-seasonalDeals-select'
            });

            this.tripService
                .getTripIdForHolidayDeal(selected, occupancy)
                .then(tripId => {
                    this.$location.path(`/holidays/hotels/${tripId}`);
                    this.$location.search('bfd', deal.Id);
                });
        }

        showMoreClick(ShowMoreDestination: string) {
            this.showMore = true;
            this.analytics.track('mixpanel', 'WasNowShowMore', { ShowMoreDestination });
        }

        watchForPageRedirection() {
            //This function protects against a bug which happens when clicking on a link in the menu.
            //e.g. <a href="holidaydeals?destination=3-54859">
            //When this happens the page must reload and start fresh.
            let wasPopulated = false;
            this.$scope.$watch(() => Object.keys(this.$location.search()).length > 2, isPopulated => {
                if (wasPopulated && !isPopulated) {
                    this.$route.reload();
                }
                wasPopulated = wasPopulated || isPopulated;
            });
        }
    }

    angular
        .module('TR')
        .controller('seasonalDeals', SeasonalDeals);
}
